import { useState, useEffect, useCallback, type ReactNode } from "react";
import {
  signIn,
  signOut as signOutAction,
  authUrl,
} from "@/services/authService";
import { env } from "@/config/env";
import { SessionContext, type Session } from "@/contexts/SessionContext";

/**
 * Fetch session from the server.
 * In preview environments, fetches directly from the BFF (bypassing Firebase
 * Hosting which strips cookies). In dev/qa/prod, uses relative URL via the LB.
 */
async function fetchSession(): Promise<Session | null> {
  try {
    const response = await fetch(authUrl("/api/auth/session"), {
      credentials: "include",
    });
    if (!response.ok) {
      return null;
    }
    const session = await response.json();
    if (!session || Object.keys(session).length === 0) {
      return null;
    }
    return session as Session;
  } catch {
    return null;
  }
}

/**
 * SessionProvider - Provides session context and handles authentication flow
 *
 * - Fetches and manages session state
 * - Auto-redirects to sign-in when unauthenticated (if auth is enabled)
 * - Handles RefreshTokenError by redirecting to sign-in
 * - Provides isAuthEnabled flag to avoid duplicate env() calls
 */
export function SessionProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [status, setStatus] = useState<
    "loading" | "authenticated" | "unauthenticated"
  >("loading");

  const isAuthEnabled = env("ENABLE_AUTHENTICATION")?.toLowerCase() === "true";

  const update = useCallback(() => {
    // Use .then() rather than async/await so the setState calls happen in a
    // nested callback (subscription-style) rather than directly in this
    // function's body, matching the pattern effects use to react to external
    // data becoming available (https://react.dev/learn/you-might-not-need-an-effect).
    return fetchSession().then((newSession) => {
      setSession(newSession);
      setStatus(newSession ? "authenticated" : "unauthenticated");
      return newSession;
    });
  }, []);

  useEffect(() => {
    void update();
  }, [update]);

  // Handle RefreshTokenError - redirect to sign-in when refresh token is invalid
  useEffect(() => {
    if (session?.error === "RefreshTokenError") {
      console.warn(
        "[Auth] Refresh token expired or revoked, redirecting to sign-in",
      );
      void signOutAction({ callbackUrl: window.location.href });
    }
  }, [session?.error]);

  // Auto-redirect to sign-in when unauthenticated
  useEffect(() => {
    if (isAuthEnabled && status === "unauthenticated") {
      void signIn("azure-ad");
    }
  }, [isAuthEnabled, status]);

  // Show loading state
  if (status === "loading") {
    return (
      <div className="flex h-full items-center justify-center font-sans">
        <p>Loading...</p>
      </div>
    );
  }

  // Show redirecting state (when auth enabled but no session yet)
  if (isAuthEnabled && !session) {
    return (
      <div className="flex h-full items-center justify-center font-sans">
        <p>Redirecting to login...</p>
      </div>
    );
  }

  return (
    <SessionContext.Provider
      value={{ data: session, status, isAuthEnabled, update }}
    >
      {children}
    </SessionContext.Provider>
  );
}
